<template>
  <HeaderComponent />
  <main class="container mx-auto px-4 py-8 md:py-12 min-h-screen">
    <div class="max-w-4xl mx-auto">
      <!-- Header Section -->
      <div class="text-center mb-12">
        <h1 class="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
          Professional Salesforce Tools
        </h1>
        <p class="text-lg text-gray-600 max-w-2xl mx-auto">
          Enhance your Salesforce CRM with our suite of powerful, easy-to-use tools designed for quick turnaround.
        </p>
      </div>

      <!-- Tools Section -->
      <div class="space-y-6 md:space-y-8">
        <!-- Web-to-Lead -->
        <section class="bg-white rounded-xl shadow-sm p-6 md:p-8 border border-gray-100">
          <div class="flex flex-col sm:flex-row gap-6">
            <div class="flex-shrink-0 flex sm:block justify-center">
              <div class="w-14 h-14 md:w-16 md:h-16 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 md:h-8 md:w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
            </div>
            <div class="flex-grow">
              <h2 class="text-xl md:text-2xl font-semibold text-gray-900 mb-3">Web-to-Lead Form Builder</h2>
              <p class="text-gray-600 mb-4 text-sm md:text-base">
                Create custom forms to capture leads directly into your Salesforce CRM. Our form builder helps you generate
                professional lead capture forms that integrate seamlessly with your Salesforce organization.
              </p>
              <div class="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <router-link
                    to="/tools/salesforce-web-to-lead-form-builder"
                    class="w-full sm:w-auto inline-flex items-center justify-center px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200"
                >
                  Build Form
                </router-link>
                <a
                    href="https://arrify.com/web-to-lead-in-salesforce"
                    class="text-blue-600 hover:text-blue-800 font-medium text-sm md:text-base"
                >
                  Learn more: Web to Lead in Salesforce - DIY in 4 steps →
                </a>
              </div>
            </div>
          </div>
        </section>

        <!-- Web-to-Case -->
        <section class="bg-white rounded-xl shadow-sm p-6 md:p-8 border border-gray-100">
          <div class="flex flex-col sm:flex-row gap-6">
            <div class="flex-shrink-0 flex sm:block justify-center">
              <div class="w-14 h-14 md:w-16 md:h-16 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 md:h-8 md:w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
            </div>
            <div class="flex-grow">
              <h2 class="text-xl md:text-2xl font-semibold text-gray-900 mb-3">Web-to-Case Form Builder</h2>
              <p class="text-gray-600 mb-4 text-sm md:text-base">
                Create Web-to-Case forms to streamline customer support in Salesforce. Build custom support forms that
                automatically create cases in your Salesforce organization and route them to the right team.
              </p>
              <div class="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <router-link
                    to="/tools/salesforce-web-to-case-form-builder"
                    class="w-full sm:w-auto inline-flex items-center justify-center px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200"
                >
                  Build Form
                </router-link>
                <!--<a
                    href="/tools/web-to-case-guide"
                    class="text-blue-600 hover:text-blue-800 font-medium text-sm md:text-base"
                >
                  Learn more: Web-to-Case Guide →
                </a>-->
              </div>
            </div>
          </div>
        </section>

        <!-- PDF Builder -->
        <section class="bg-white rounded-xl shadow-sm p-6 md:p-8 border border-gray-100">
          <div class="flex flex-col sm:flex-row gap-6">
            <div class="flex-shrink-0 flex sm:block justify-center">
              <div class="w-14 h-14 md:w-16 md:h-16 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 md:h-8 md:w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
            <div class="flex-grow">
              <h2 class="text-xl md:text-2xl font-semibold text-gray-900 mb-3">PDF Builder</h2>
              <p class="text-gray-600 mb-4 text-sm md:text-base">
                Generate professional PDF documents from your Salesforce data effortlessly. Create customized PDFs for
                quotes, invoices, contracts, and other business documents with our intuitive PDF builder.
              </p>
              <div class="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <router-link
                    to="/tools/salesforce-pdf-builder"
                    class="w-full sm:w-auto inline-flex items-center justify-center px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200"
                >
                  Build PDF
                </router-link>
                <a
                    href="https://arrify.com/generate-pdf-in-salesforce"
                    class="text-blue-600 hover:text-blue-800 font-medium text-sm md:text-base"
                >
                  Learn more: Generate PDF in Salesforce For free in 2024 →
                </a>
              </div>
            </div>
          </div>
        </section>

        <!-- Additional Links -->
        <!--<div class="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-center mt-8 text-sm md:text-base">
          <a href="/blog" class="text-blue-800 hover:text-blue-600 font-medium">
            Explore Blog Posts
          </a>
          <a href="/connect/sfdc-partner" class="text-blue-800 hover:text-blue-600 font-medium">
            Find Saleforce Partner
          </a>
          <a href="/connect/sfdc-expert" class="text-blue-800 hover:text-blue-600 font-medium">
            Find Saleforce Expert
          </a>
        </div> -->

        <!-- Find Saleforce Partner -->
        <section class="bg-white rounded-xl shadow-sm p-6 md:p-8 border border-gray-100">
          <div class="flex flex-col sm:flex-row gap-6">
            <div class="flex-shrink-0 flex sm:block justify-center">
              <div class="w-14 h-14 md:w-16 md:h-16 bg-blue-100 rounded-lg flex items-center justify-center">
                  <!--  use related svg -->
                <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 md:h-8 md:w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 4h5V2H7a2 2 0 00-2 2v5h5a2 2 0 002-2zM16 7h-5V5h5a2 2 0 012 2v5h-5a2 2 0 00-2-2zM9 13h6v4H9a2 2 0 00-2-2v-5h5a2 2 0 012 2z" />
                </svg>
              </div>
            </div>
            <div class="flex-grow">
              <h2 class="text-xl md:text-2xl font-semibold text-gray-900 mb-3">Find Saleforce Partner</h2>
              <p class="text-gray-600 mb-4 text-sm md:text-base">
                Looking to integrate Salesforce into your business? Partner with certified Salesforce experts who can help you implement, customize, and optimize Salesforce solutions tailored to your organization's needs. Connect with trusted partners to enhance your Salesforce experience.
              </p>
              <div class="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <router-link
                    to="/connect/sfdc-partner"
                    class="w-full sm:w-auto inline-flex items-center justify-center px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200"
                >
                SFDC Partner
                </router-link>
                <a
                    href="/tools/pdf-generation-guide"
                    class="text-blue-600 hover:text-blue-800 font-medium text-sm md:text-base"
                >
                 <!-- Learn more: Saleforce Partner →-->
                </a>
              </div>
            </div>
          </div>
        </section>

        <!-- Find Saleforce Expert -->
        <section class="bg-white rounded-xl shadow-sm p-6 md:p-8 border border-gray-100">
          <div class="flex flex-col sm:flex-row gap-6">
            <div class="flex-shrink-0 flex sm:block justify-center">
              <div class="w-14 h-14 md:w-16 md:h-16 bg-blue-100 rounded-lg flex items-center justify-center">
                <!--  use related svg -->
                <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 md:h-7 md:w-7 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 11l3 3L22 4M4 4h12a2 2 0 012 2v16a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2z" />
                </svg>
              </div>
            </div>
            <div class="flex-grow">
              <h2 class="text-xl md:text-2xl font-semibold text-gray-900 mb-3">Find Saleforce Expert</h2>
              <p class="text-gray-600 mb-4 text-sm md:text-base">
                Need expert guidance on your Salesforce projects? Whether you're looking for strategy, customization, or troubleshooting, connect with top-tier Salesforce professionals who bring deep expertise to help you achieve your goals. Find experienced Salesforce experts to streamline your business processes.
              </p>
              <div class="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <router-link
                    to="/connect/sfdc-expert"
                    class="w-full sm:w-auto inline-flex items-center justify-center px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200"
                >
                SFDC Expert
                </router-link>
                <a
                    href="https://cms.arrify.com/hire-salesforce-expert/"
                    class="text-blue-600 hover:text-blue-800 font-medium text-sm md:text-base"
                >
                Hire Salesforce Expert 101 - What You Need to Know
                </a>
              </div>
            </div>
          </div>
        </section>

      </div>
    </div>
  </main>
  <FooterComponent />
</template>

<script setup>
import {useCommonHead} from '../composables/useCommonHead';
import HeaderComponent from "../../components/Header.vue";
import FooterComponent from "../../components/Footer.vue";
const pageTitle = 'Professional Salesforce Tools | Web-to-Lead, PDF & Case Builders';
const pageDescription = 'Enhance your Salesforce CRM with our professional suite of tools. Create Web-to-Lead forms, generate PDFs, and build Web-to-Case forms efficiently.';
const currentUrl = '/tools';

useCommonHead(pageTitle, pageDescription, currentUrl);
</script>


<style scoped>

</style>